import type { IAuditLog } from '../../../src/engine/AuditLog';
import type { IGraphClient } from '../../../src/services/GraphService';
import type { IWorkspace } from '../../api/src/workspace';
import { GraphAuditLog } from '../GraphAuditLog';
import { type IFields, ListStore } from '../lists/ListStore';
import { clientView, type IClientView } from './model';
import { RecordWriter } from '../lists/RecordWriter';
import { APPLICATIONS, CHECKLIST, CLIENTS, EVENTS, PEOPLE, RESTRICTED_HISTORY, restrictedSchema } from './schemas';

/** One application's lists: the firm's main ones, or the restricted ones. */
export class RecordSet {
  public readonly clients: ListStore;
  public readonly applications: ListStore;
  public readonly people: ListStore;
  public readonly checklist: ListStore;
  public readonly events: ListStore;
  public readonly writer: RecordWriter;

  constructor(
    graph: IGraphClient,
    manageGraph: IGraphClient,
    siteId: string,
    public readonly restricted: boolean,
    public readonly audit: IAuditLog,
    /** Where this set's files are (the main library, or the restricted one). */
    public readonly library: { driveId: string; libraryName: string },
    user: string
  ) {
    const s = restricted ? restrictedSchema : (x: typeof APPLICATIONS) => x;
    this.clients = new ListStore(graph, manageGraph, siteId, s(CLIENTS));
    this.applications = new ListStore(graph, manageGraph, siteId, s(APPLICATIONS));
    this.people = new ListStore(graph, manageGraph, siteId, s(PEOPLE));
    this.checklist = new ListStore(graph, manageGraph, siteId, s(CHECKLIST));
    this.events = new ListStore(graph, manageGraph, siteId, s(EVENTS));
    this.writer = new RecordWriter(audit, this.lists(), { libraryId: library.driveId, libraryName: library.libraryName, user });
  }

  public lists(): ListStore[] {
    return [this.clients, this.applications, this.people, this.checklist, this.events];
  }
}

/**
 * The firm's records in its own site: clients, and the main set of application lists, plus the
 * restricted set when the firm has set it up (its own lists, library and history, open only to the
 * "Restricted files" group). Reading works with the everyday sign-in; creating the lists and writing
 * need the site-management permission, asked for the first time the person does something that writes.
 */
export class FirmRecords {
  public readonly main: RecordSet;
  public readonly restricted: RecordSet | undefined;

  constructor(graph: IGraphClient, manageGraph: IGraphClient, audit: IAuditLog, workspace: IWorkspace, user: string, siteWebUrl: string = '') {
    this.main = new RecordSet(graph, manageGraph, workspace.siteId, false, audit, workspace, user);
    const r = workspace.firm?.restricted;
    this.restricted = r
      ? new RecordSet(graph, manageGraph, workspace.siteId, true, new GraphAuditLog(graph, manageGraph, workspace.siteId, siteWebUrl, RESTRICTED_HISTORY), r, user)
      : undefined;
  }

  // The main set, for code that doesn't deal with restricted files.
  public get clients(): ListStore { return this.main.clients; }
  public get applications(): ListStore { return this.main.applications; }
  public get people(): ListStore { return this.main.people; }
  public get checklist(): ListStore { return this.main.checklist; }
  public get events(): ListStore { return this.main.events; }
  public get writer(): RecordWriter { return this.main.writer; }

  public all(): ListStore[] {
    return this.main.lists();
  }

  /** Creates any list that's missing (and any column a list is missing). Safe to run again. */
  public async ensureAll(onProgress?: (title: string) => void): Promise<void> {
    for (const s of this.all()) {
      onProgress?.(s.schema.title);
      await s.ensure();
    }
  }

  /** Both sets, the restricted one only when the person can open it. */
  public sets(): RecordSet[] {
    return this.restricted ? [this.main, this.restricted] : [this.main];
  }

  /**
   * Rows from both sets, together (the restricted set skipped when the person can't open it or it
   * isn't there).
   */
  public async fromSets<T>(read: (set: RecordSet) => Promise<T[]>): Promise<T[]> {
    const out: T[] = await read(this.main);
    if (this.restricted) {
      out.push(...await read(this.restricted).catch(() => [] as T[]));
    }
    return out;
  }

  /** Every client, from both sets (restricted clients only when the person can open them). */
  public async allClients(): Promise<IClientView[]> {
    const out: IClientView[] = [];
    for (const set of this.sets()) {
      const rows = await set.clients.all().catch(e => { if (!set.restricted) { throw e; } return []; });
      out.push(...rows.map(r => ({ ...clientView(r), restricted: set.restricted })));
    }
    return out;
  }

  /** One client by key, and the set it's kept in. */
  public async findClient(key: string): Promise<IClientView | undefined> {
    for (const set of this.sets()) {
      const r = await set.clients.get(key).catch(() => undefined);
      if (r && r.fields.Removed !== true) {
        return { ...clientView(r), restricted: set.restricted };
      }
    }
    return undefined;
  }

  /** The set a client is kept in. */
  public setOf(client: { restricted?: boolean }): RecordSet {
    return client.restricted && this.restricted ? this.restricted : this.main;
  }

  /**
   * Moves a client to the restricted clients list, for a refugee or private-sponsorship file. The full
   * record (same key) is written there, in the restricted history; the main row is set aside with its
   * personal details cleared, without a main History entry, so nothing about the client is left in the
   * main lists. Safe to run again.
   */
  public async moveToRestricted(client: IClientView): Promise<IClientView> {
    const target: RecordSet | undefined = this.restricted;
    if (!target) {
      throw new Error('Restricted files aren\'t set up yet. A site owner can set them up in Settings.');
    }
    if (client.restricted) {
      return client;
    }
    await this.requireRestrictedClients();
    let moved = await target.clients.get(client.key);
    if (!moved) {
      const fields: IFields = {};
      for (const c of CLIENTS.columns) {
        if (client.rec.fields[c.name] !== undefined && client.rec.fields[c.name] !== '') {
          fields[c.name] = client.rec.fields[c.name];
        }
      }
      moved = await target.writer.batch().create(target.clients, { ...fields, Title: client.name, Key: client.key }, client.name, `Moved the client ${client.name} to restricted files`);
    }
    const cleared: IFields = { Title: 'Moved to restricted files', Removed: true };
    for (const c of CLIENTS.columns) {
      cleared[c.name] = undefined;
    }
    await this.main.clients.update(client.rec.id, cleared);
    return { ...clientView(moved), restricted: true };
  }

  /**
   * The restricted clients list has to come from "Set up restricted files" (it gets its own permissions there);
   * one created on the fly would be open to everyone on the site. A firm that set up restricted files before
   * this list existed presses "Set up restricted files" again.
   */
  public async requireRestrictedClients(): Promise<void> {
    if (!this.restricted || !(await this.restricted.clients.exists())) {
      throw new Error('The restricted clients list isn\'t set up yet. A site owner can press "Set up restricted files" again in Settings to add it.');
    }
  }

  /** The set an application is in, found by its key (restricted lists the person can't open are skipped). */
  public async find(applicationKey: string): Promise<{ set: RecordSet; record: import('../lists/ListStore').IRecord } | undefined> {
    for (const set of this.sets()) {
      const record = await set.applications.get(applicationKey).catch(() => undefined);
      if (record && record.fields.Removed !== true) {
        return { set, record };
      }
    }
    return undefined;
  }
}
